import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useToastContext } from '@/contexts/toastContext';
import useApiSubmit from '@/hooks/useApiSubmit';
import useCandidateStatuses from '@/hooks/useCandidateStatuses';
import candidatesServices, { CandidateResponse, CandidateParams } from '@/services/candidates';
import UpdateCandidateStatusComponent from './UpdateCandidateStatus.component';
import { Status } from '../AutoCompleteGenericElements';

type UpdateCandidateStatusProps = {
  isDisabled: boolean;
  isAlreadyUpdated?: boolean;
  currentStatus?: string;
  availableStatuses?: any[];
  onRefetchInterview: () => void;
};

export default function UpdateCandidateStatus({
  isDisabled = true,
  isAlreadyUpdated = false,
  currentStatus,
  availableStatuses,
  onRefetchInterview,
}: UpdateCandidateStatusProps): JSX.Element {
  const { id: candidateId } = useParams();
  const toast = useToastContext();

  const [isOpen, setIsOpen] = useState(false);
  const [currentStatusOption, setCurrentStatusOption] = useState(currentStatus);

  const candidateStatuses = useCandidateStatuses();

  const updateCandidateMutation = useApiSubmit<CandidateResponse, CandidateParams>(
    candidatesServices.updateCandidateById
  );

  const statusOptions = useMemo(() => {
    if (availableStatuses) {
      return availableStatuses;
    }

    return candidateStatuses.map((groupedStatuses) => ({
      ...groupedStatuses,
      options: groupedStatuses.options.map((status) => ({
        value: status,
        node: (elementProps) => <Status {...elementProps} key={status} status={status} />,
      })),
    }));
  }, [availableStatuses, candidateStatuses]);

  const onToggleIsOpen = (): void => {
    setIsOpen((prevIsOpen) => !prevIsOpen);
  };

  const onChangeStatus = (newStatus: string): void => {
    setCurrentStatusOption(newStatus);
  };

  const onSaveNewStatus = async (): Promise<void> => {
    const params = {
      candidate: {
        id: candidateId,
        status: currentStatusOption,
      },
    };

    try {
      await updateCandidateMutation.mutateAsync(params);
      onRefetchInterview();
      onToggleIsOpen();
      toast.showSuccessFeedback('The candidate status has been updated.');
    } catch {
      toast.showErrorFeedback('There was a problem to update the candidate status.');
    }
  };

  const isUpdated = isAlreadyUpdated || updateCandidateMutation.isSuccess;

  return (
    <UpdateCandidateStatusComponent
      isUpdated={isUpdated}
      isDisabled={isDisabled}
      isOpen={isOpen}
      statuses={statusOptions}
      currentStatus={currentStatusOption}
      onChangeStatus={onChangeStatus}
      onSaveNewStatus={onSaveNewStatus}
      onToggleIsOpen={onToggleIsOpen}
    />
  );
}
